/**
 * Design tokens: the single source of truth for colors, typography, radii and layout.
 * Shared read these rather than hard-coding literals.
 *
 * The direction carries no shadow: depth comes from tinted surfaces and hairline borders.
 * Its neutrals are cool — every grey is biased toward the teal accent, never a pure grey.
 */

/**
 * Extracted so `appBar`, below, can reference an existing role (`colors.surfaceInverted`,
 * `colors.recording`, `colors.accentBright`, `colors.inkOnAccentBright`) instead of repeating its
 * literal — a plain object literal cannot reference a sibling key of the same literal.
 */
const colors = {
	/** Page background. */
	canvas: '#E7f0F3',
	/** Raised block (card, panel, dialog). */
	surface: '#FFFFFF',
	/** Recessed block inside a surface (stat strip, quote, inert field). */
	surfaceMuted: '#F1F4F5',
	/** Editable value: reads as a field without looking like an input. */
	surfaceInset: '#FAFCFC',
	/** Dark surface: recording screen, transcript player, toast. */
	surfaceInverted: '#2B3A45',
	/** Unfilled part of a progress bar or a disabled action. */
	track: '#D8E0E3',
	trackMuted: '#E9EEF0',

	ink: '#2B3A45',
	inkSecondary: '#4E6673',
	inkMuted: '#6E8290',
	inkFaint: '#8A9197',
	inkOnDark: '#E9EEF0',
	inkOnDarkMuted: '#8A9197',

	accent: '#1F8FA8',
	accentDark: '#15697D',
	/** Accent-tinted surface: header of a beneficiary file, invitation form. */
	accentSurface: '#E0F1F5',
	/** Same tint, one step up: a selected option. */
	accentSurfaceStrong: '#E7F2F5',
	/**
	 * The accent is unreadable on the inverted surface: a dark screen carries this lighter
	 * cyan instead, and anything filled with it takes `inkOnAccentBright` as its text.
	 */
	accentBright: '#9FE0EF',
	inkOnAccentBright: '#123A44',

	border: 'rgba(43, 58, 69, 0.07)',
	borderStrong: 'rgba(43, 58, 69, 0.12)',
	/** Dashed outline of a value waiting to be edited. */
	borderDashed: 'rgba(43, 58, 69, 0.14)',

	/** Capture in progress: the only raspberry that means "recording". */
	recording: '#E8417A',
	/** Live waveform bars. */
	waveform: '#F5A623',
	/** Something checked and settled. */
	validated: '#2E9A80',
} as const;

/**
 * Extracted for the same reason as `colors`: `appBar` reuses `cyan`, `green`, `amber` and
 * `raspberry` by reference instead of repeating their literal.
 */
const brandMark = {
	amber: '#F5A623',
	green: '#3FB89A',
	cyan: '#3FBEDC',
	raspberry: '#E8417A',
} as const;

/**
 * Surface/ink pairs carrying a state: an information in a generated document, the health of a
 * service, the outcome of a connection test. A pair is used whole — its ink is legible on its
 * surface, nowhere else.
 *
 * `solid` is the flat fill under white text. It is darker than the ink on purpose: `warning`'s
 * ink holds 3.4:1 on its own surface, which is enough for a tinted block, and only 3.9:1 under
 * white, which is below AA for body text. `bright` is the light variant, legible on
 * `colors.surfaceInverted`: it carries the state on a dark surface, icon only — the way
 * `colors.accentBright` carries the accent. Extracted for the same reason as `colors` and
 * `brandMark`: `appBar` reuses `success.solid` by reference instead of repeating its literal.
 */
const states = {
	success: { surface: '#DFF2EC', ink: '#1F6E5C', solid: '#1C6E5B', bright: '#7FD6B8' },
	warning: { surface: '#FDEFD6', ink: '#B5730D', solid: '#8A5A0A', bright: '#F2C27A' },
	danger: { surface: '#FBE2EA', ink: '#C42D5C', solid: '#A81E4A', bright: '#F59AB6' },
} as const;

/**
 * Identity tints, extracted so `aiCosts` can reuse them by reference.
 */
const tints = {
	teal: { surface: '#D6EDF3', ink: '#15697D', solid: '#14647A' },
	green: { surface: '#D6F0E8', ink: '#1F6E5C', solid: '#1C6E5B' },
	amber: { surface: '#FCE7C7', ink: '#8A5A0A', solid: '#8A5A0A' },
	raspberry: { surface: '#FBDCE6', ink: '#A81E4A', solid: '#A81E4A' },
} as const;

/**
 * Families are self-hosted through Fontsource (imported in `main.tsx`); the display name is the one
 * the variable package declares.
 * Extracted for the same reason as `colors`: `typography`, below, reuses these by reference
 * instead of repeating their literal.
 */
const fonts = {
	display: "'Bricolage Grotesque Variable', system-ui, sans-serif",
	body: "'Public Sans', system-ui, -apple-system, 'Segoe UI', sans-serif",
	/** Figures, durations and uppercase eyebrows. */
	mono: "'JetBrains Mono', ui-monospace, 'SFMono-Regular', monospace",
} as const;

const fontSizes = {
	/** Field label, legend. */
	small: '0.75rem',
	/** Body copy, field value, button. */
	medium: '0.875rem',
	large: '1rem',
} as const;

const fontWeights = {
	normal: 400,
	medium: 500,
	semibold: 600,
	bold: 700,
} as const;

/**
 * Named typographic roles: each one is a complete recipe — family, size, weight, and the
 * `letterSpacing` / `lineHeight` when they carry meaning — for one intent (a page title, a card
 * title, a brand lockup…), never a bare size. A component reads a role as a whole; it never
 * reassembles one from `fontSizes` plus a literal weight or spacing.
 */
const typography = {
	/** The "Dixit" brand lockup, standalone wherever the app names itself (nav, login, footer). */
	wordmark: {
		fontFamily: fonts.display,
		fontSize: '1.0625rem',
		fontWeight: fontWeights.bold,
		letterSpacing: '-0.02em',
		lineHeight: 1,
	},
	/** Largest display line of the home page. */
	heroTitle: {
		fontFamily: fonts.display,
		fontSize: 'clamp(2rem, 5vw, 2.625rem)',
		fontWeight: fontWeights.semibold,
		letterSpacing: '-0.035em',
		lineHeight: 1.05,
	},
	/** Home page section title, one step below the hero. */
	sectionTitle: {
		fontFamily: fonts.display,
		fontSize: 'clamp(1.75rem, 3.2vw, 2.125rem)',
		fontWeight: fontWeights.semibold,
		letterSpacing: '-0.03em',
		lineHeight: 1.15,
	},
	/** `PageHeader`'s title — the marker atop every application screen. */
	pageTitle: {
		fontFamily: fonts.display,
		fontSize: '2.75rem',
		fontWeight: fontWeights.semibold,
		letterSpacing: '-0.035em',
		lineHeight: 1.2,
	},
	/** Title of a card in a list (facility, organization, role, user…). */
	cardTitle: {
		fontFamily: fonts.display,
		fontSize: '1.125rem',
		fontWeight: fontWeights.semibold,
		letterSpacing: '-0.01em',
		lineHeight: 1.25,
	},
	/** Title of an empty/error state placeholder. */
	stateTitle: {
		fontFamily: fonts.display,
		fontSize: '1.0625rem',
		fontWeight: fontWeights.semibold,
	},
	/** Small uppercase monospace marker above a title or a section (`PageHeader`'s eyebrow). */
	eyebrow: {
		fontFamily: fonts.mono,
		fontSize: '0.7875rem',
		fontWeight: fontWeights.normal,
		letterSpacing: '0.12em',
		lineHeight: 1,
	},
	/** Label inside a pill-shaped navigation chip. */
	chip: {
		fontFamily: fonts.mono,
		fontSize: fontSizes.small,
		fontWeight: fontWeights.medium,
		letterSpacing: '0.04em',
		lineHeight: 1,
	},
	/** Large monospace figure (a stat, a duration) set off from body copy. */
	metric: {
		fontFamily: fonts.mono,
		fontSize: '1.375rem',
		fontWeight: fontWeights.medium,
		lineHeight: 1,
	},
} as const;

export const theme = {
	colors,

	states,

	/**
	 * Identity tints: they distinguish people and classifications, and mean nothing about a state.
	 * Each carries three paintings — a tinted surface, its ink, and a solid fill whose text is
	 * always white. An identity tile takes the solid; a tag next to it takes surface + ink.
	 */
	tints,

	/** Logo colors. They belong to the mark and appear nowhere else in the interface. */
	brandMark,

	/** Identity color of a subject type, keyed by the color token the server sends. */
	subjectTypeColors: {
		cyan: brandMark.cyan,
		teal: tints.teal.solid,
		green: tints.green.solid,
		amber: tints.amber.solid,
		raspberry: tints.raspberry.solid,
		slate: colors.inkSecondary,
	},

	fonts,

	fontSizes,

	fontWeights,

	typography,

	/** Geometry of an input, a select or a button — the direction breathes wider than a dense form. */
	controls: {
		height: '3rem',
		heightSmall: '2.5rem',
		heightLarge: '3.5rem',
		padding: '0.875rem 1.0625rem',
	},

	motion: {
		duration: '0.25s',
	},

	radii: {
		/** Square control: checkbox, small state box. */
		xsmall: '8px',
		small: '14px',
		medium: '16px',
		large: '20px',
		xlarge: '22px',
		xxlarge: '28px',
		pill: '999px',
	},

	layout: {
		maxWidth: '1050px',
		gutter: '1.5rem',
		/** Below this width the interface goes single-column: the top-bar navigation and the settings panel give way to a drawer opened from the top bar, which keeps the agency switcher. */
		compactBreakpoint: '880px',
		/** Width of a `left`/`right` `Sidebar` panel — bound onto `--ui-kit-sidebar-width`. */
		sidebarWidth: '35rem',
		/** Width of the settings panel; `AppMain` reads it to keep the page beside a pinned panel. */
		settingsPanelWidth: 'min(22rem, 90vw)',

		/**
		 * The widths a page may narrow itself to, through `PageContainer`'s `width` prop. `maxWidth`
		 * above stays the ceiling — a page narrows below it, never past it, so its title keeps the axis
		 * the header and the section tabs sit on.
		 *
		 * `sm` and `md` sit under the ~1048px a card grid needs to hold three columns
		 * (`auto-fill minmax(20rem, 1fr)`), so they drop it to two. That is the point of a narrow page,
		 * not a side effect to work around.
		 */
		pageWidths: {
			/** Single column: a form, a settings screen, one block of reading. */
			sm: '800px',
			/** Two columns of cards, or a form beside a summary. */
			md: '1000px',
			/** Just inside the ceiling: a dense page that still wants a margin off the header's edge. */
			xl: '1200px',
		},
	},

	/** Home page: indicator card bands, per-type bar series and shortcut tiles, taken from the reference mock-up. */
	home: {
		indicatorBand: {
			documents: [brandMark.cyan, brandMark.green, brandMark.amber, brandMark.raspberry],
			beneficiaries: brandMark.green,
			costs: brandMark.amber,
		},
		/** Bar fill by rank in the per-type breakdown; `dimmed` while another type is selected. */
		typeSeries: [
			{ fill: colors.accent, dimmed: '#A9D6E1' },
			{ fill: colors.validated, dimmed: '#A8DCCB' },
			{ fill: brandMark.amber, dimmed: '#FAD597' },
			{ fill: brandMark.raspberry, dimmed: '#F5B3C9' },
		],
		/** "Autres types" row and archived types. */
		typeSeriesOther: { fill: '#9AA4AB', dimmed: '#D3D9DC' },
		shortcutTiles: { beneficiaries: colors.validated, workflowRuns: '#D98A0B' },
	},

	/**
	 * Costs screen: audio reads in the teal family and documents in the amber family wherever both
	 * appear, and the summary card is closed on top by the four-band brand stripe.
	 */
	aiCosts: {
		summaryStripe: [brandMark.cyan, brandMark.green, brandMark.amber, brandMark.raspberry],
		audio: { bar: colors.accent, ...tints.teal },
		documents: { bar: brandMark.amber, ...tints.amber },
		/** Tag of a super-administrator-only tab. */
		superAdminTag: tints.raspberry,
		/** Closing row of a table: recessed surface, ruled off by an ink line. */
		totalRow: { background: colors.surfaceMuted, rule: colors.ink },
	},

	/**
	 * The app shell's top bar: a light surface, closed by the four-band brand stripe, with a hairline
	 * border standing in for dark chrome. `navChip` / `navChipActive` are the pill-shaped tab pair at
	 * rest and current; the avatar badge takes an `accentDark` fill under white text to hold AA
	 * contrast.
	 */
	appBar: {
		background: colors.surface,
		/** Hairline under the bar. */
		border: colors.border,
		/** Four-band stripe closing the header. */
		brandStripe: {
			cyan: brandMark.cyan,
			green: brandMark.green,
			amber: brandMark.amber,
			raspberry: brandMark.raspberry,
		},
		/** Pill tab or inert chip, at rest. */
		navChip: {
			background: colors.canvas,
			text: colors.inkSecondary,
		},
		/** Pill tab, current. */
		navChipActive: {
			background: colors.accent,
			text: colors.surface,
		},
		facilitySwitcher: {
			background: colors.accentSurface,
			text: colors.accentDark,
		},
		avatarBadge: {
			background: colors.accentDark,
			text: colors.surface,
		},
	},

	/**
	 * The permanent navigation menu (and its drawer on narrow viewports): a white column closed by a
	 * hairline, section eyebrows in muted ink, and a current entry tinted with the accent surface.
	 */
	navigationMenu: {
		background: colors.surface,
		border: colors.border,
		/** Section eyebrow. */
		sectionLabel: colors.inkMuted,
		item: {
			text: colors.inkSecondary,
			icon: colors.inkMuted,
			hoverBackground: colors.surfaceMuted,
			hoverText: colors.ink,
		},
		itemActive: {
			background: colors.accentSurface,
			text: colors.accentDark,
			icon: colors.accent,
		},
	},
} as const;
